import React, { useState } from 'react';
import { Box, VStack, HStack, Text, Portal, Image, Icon } from '@chakra-ui/react';
import { MdInstallMobile } from 'react-icons/md';
import { useTranslation } from '../../hooks/useTranslation';
import { isAndroid, isIOS, isStandalone } from '../../utils/deviceUtils';
import Button from '../base/Button';
import InstructionStepItem from './InstructionStepItem';

const STORAGE_KEY = 'installPromptSkipCount';
const MAX_SKIPS = 5;

export const getInstallPromptSkipCount = (): number => {
    if (typeof window === 'undefined') return 0;
    const raw = localStorage.getItem(STORAGE_KEY);
    const n = parseInt(raw ?? '0', 10);
    return isNaN(n) ? 0 : n;
};

export const incrementInstallPromptSkip = (): number => {
    const next = getInstallPromptSkipCount() + 1;
    localStorage.setItem(STORAGE_KEY, String(next));
    return next;
};

export const shouldShowInstallPrompt = (): boolean => {
    if (isStandalone()) return false;
    return getInstallPromptSkipCount() < MAX_SKIPS;
};

interface InstallAppModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const InstallAppModal: React.FC<InstallAppModalProps> = ({ isOpen, onClose }) => {
    const { t, currentLanguage } = useTranslation();
    const [view, setView] = useState<'prompt' | 'instructions'>('prompt');

    const handleSkip = () => {
        incrementInstallPromptSkip();
        onClose();
    };

    const handleInstall = () => {
        setView('instructions');
    };

    const handleCloseInstructions = () => {
        setView('prompt');
        onClose();
    };

    if (!isOpen) return null;

    const isIOSDevice = isIOS();
    const isAndroidDevice = isAndroid();

    return (
        <Portal>
            <Box
                position="fixed"
                inset={0}
                zIndex={3000}
                bg="white"
                display="flex"
                flexDirection="column"
                overflow="auto"
            >
                {view === 'prompt' ? (
                    <>
                        <VStack flex={1} justify="center" px={6} py={8} gap={6}>
                            <Image
                                src="/favicon-192x192.png"
                                alt="Office Hof"
                                w="72px"
                                h="72px"
                                objectFit="contain"
                            />
                            <HStack>
                                <Text fontSize="xl" fontWeight="bold" textAlign="center">
                                    {t.installPrompt.title}
                                </Text>
                                <Icon as={MdInstallMobile} boxSize={5} color="black" />
                            </HStack>
                            <Text fontSize="md" color="black" textAlign="center" maxW="320px">
                                {t.installPrompt.description}
                            </Text>
                            <VStack w="full" gap={3} maxW="320px">
                                <Button buttonType="success" w="full" size="lg" onClick={handleInstall}>
                                    {t.installPrompt.install}
                                </Button>
                                <Button buttonType="secondary" w="full" size="lg" onClick={handleSkip}>
                                    {t.installPrompt.skip}
                                </Button>
                            </VStack>
                        </VStack>
                    </>
                ) : (
                    <>
                        <Box p={4} flex={1} overflow="auto">
                            <VStack align="stretch" gap={6}>
                                {isAndroidDevice && (
                                    <Box>
                                        <Text fontSize="lg" fontWeight="bold" mb={3}>
                                            {t.installPrompt.androidTitle}
                                        </Text>
                                        <VStack align="stretch" gap={2} pl={2}>
                                            {t.installPrompt.androidSteps.map((step, i) => (
                                                <InstructionStepItem
                                                    key={i}
                                                    number={i + 1}
                                                    text={step}
                                                    imageSrc={
                                                        '/AppModal/android/' +
                                                        currentLanguage +
                                                        '/step_' +
                                                        (i + 1) +
                                                        '.jpg'
                                                    }
                                                />
                                            ))}
                                        </VStack>
                                    </Box>
                                )}
                                {isIOSDevice && (
                                    <Box>
                                        <Text fontSize="lg" fontWeight="bold" mb={3}>
                                            {t.installPrompt.iosTitle}
                                        </Text>
                                        <VStack align="stretch" gap={2} pl={2}>
                                            {t.installPrompt.iosSteps.map((step, i) => (
                                                <InstructionStepItem
                                                    key={i}
                                                    number={i + 1}
                                                    text={step}
                                                    imageSrc={
                                                        '/AppModal/ios/' +
                                                        currentLanguage +
                                                        '/step_' +
                                                        (i + 1) +
                                                        '.jpg'
                                                    }
                                                />
                                            ))}
                                        </VStack>
                                    </Box>
                                )}
                                {!isAndroidDevice && !isIOSDevice && (
                                    <>
                                        <Box>
                                            <Text fontSize="lg" fontWeight="bold" mb={3}>
                                                {t.installPrompt.androidTitle}
                                            </Text>
                                            <VStack align="stretch" gap={2} pl={2}>
                                                {t.installPrompt.androidSteps.map((step, i) => (
                                                    <InstructionStepItem key={i} number={i + 1} text={step} />
                                                ))}
                                            </VStack>
                                        </Box>
                                        <Box>
                                            <Text fontSize="lg" fontWeight="bold" mb={3}>
                                                {t.installPrompt.iosTitle}
                                            </Text>
                                            <VStack align="stretch" gap={2} pl={2}>
                                                {t.installPrompt.iosSteps.map((step, i) => (
                                                    <InstructionStepItem key={i} number={i + 1} text={step} />
                                                ))}
                                            </VStack>
                                        </Box>
                                    </>
                                )}
                            </VStack>
                        </Box>
                        <Box p={4} borderTopWidth="1px" borderColor="gray.200">
                            <Button
                                buttonType="secondary"
                                w="full"
                                size="lg"
                                onClick={handleCloseInstructions}
                            >
                                {t.installPrompt.close}
                            </Button>
                        </Box>
                    </>
                )}
            </Box>
        </Portal>
    );
};

export default InstallAppModal;
